import React from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody} from "reactstrap";
import ButtonIcon from "../../atoms/button/buttonIcon";
import ModalDetailContent from "../../molecules/modalDetailContent/modalDetailContent";
import EventTimeAndDateFormat from "../../atoms/eventTimeAndDateFormat/eventTimeAndDateFormat";
import EventDetails from "../../../stores/domainStore/pages/models/eventDetails";
import {useTranslation} from "react-i18next";
import {isWebLink} from "../../../utils/stringUtils";
import {IconSource} from "../../../shared/enums/iconSource";
import "./eventDetailModal.sass";
import Tag from "../../atoms/tag/tag";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import Banner from "../../atoms/banner/banner";
import {MultiVillageHelper} from "../../../shared/multiVillageHelper";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import EventModalHeader from "../../molecules/eventModalHeader/eventModalHeader";
import {useStores} from "../../../stores/useStores";

export interface IEventDetailModalProps {
  eventDetails: EventDetails | null;
  eventIsReleased: boolean;
  isLoading: boolean;
  isOpen: boolean;
  toggle: () => void;
  onEditEventClick: () => void;
  onViewEventClick: () => void;
  switchToManageBooking: () => void;
  isMultiVillageOptionSelected?: boolean;
  switchToEditEventSeries: () => void;
  switchToViewEventSeries: () => void;
  currentDateTime?: Date;
  isEnhancedCalendarEvent?: boolean;
}

const EventDetailModal: React.FC<IEventDetailModalProps> = ({
  eventDetails,
  eventIsReleased,
  isLoading,
  isOpen,
  toggle,
  onEditEventClick,
  onViewEventClick,
  switchToManageBooking,
  isMultiVillageOptionSelected,
  switchToEditEventSeries,
  switchToViewEventSeries,
  currentDateTime,
  isEnhancedCalendarEvent
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const {
    uiStore: {accountStore}
  } = useStores();
  const isReleased = isMultiVillageOptionSelected
    ? eventDetails?.isReleased
    : eventIsReleased;

  if (!eventDetails) {
    return <></>;
  }

  const isEditable =
    eventDetails?.eventDateDetails.startDate.date! &&
    !eventDetails?.isPassedEndDate;
  const totalBookings = eventDetails?.bookingSpacesDetails.totalBookings || 0;
  const paidPercentage = eventDetails?.hasPaidAttendees
    ? Math.ceil(
        (eventDetails.bookingSpacesDetails.totalPaidBookings / totalBookings) *
          100
      )
    : 0;
  const totalSpace = eventDetails?.bookingSpacesDetails.totalSpace.number || 0;
  const totalBookingsPercentage = Math.ceil((totalBookings / totalSpace) * 100);
  const shouldShowRemainingSpaces =
    isEditable &&
    !eventDetails?.bookingSpacesDetails.isEventHasNoBookingLimit() &&
    totalBookingsPercentage >= 70;
  const confirmedBookingCount =
    totalBookings > 0 ? (
      <div className="booking-detail">
        <div>
          {`${totalBookings} ${
            eventDetails?.bookingSpacesDetails.isEventHasNoBookingLimit()
              ? ""
              : `/ ${eventDetails?.bookingSpacesDetails.totalSpace.number} `
          }${t("event:labels.bookingStatus.confirmed")}`}
        </div>
        <div className="extra-info">
          {`${eventDetails?.bookingSpacesDetails.totalAppBooking} ${t(
            "event:labels.bookingSource.app"
          )} | ${eventDetails?.bookingSpacesDetails
            .totalStaffAssistedBookings} ${t(
            "event:labels.bookingSource.staffAssisted"
          )}`}
        </div>
      </div>
    ) : (
      <div>{`0 ${t("event:labels.bookingStatus.confirmed")}`}</div>
    );

  const villageList = eventDetails.villages.selectedItems.map((i) => i.label);

  const viewOrEditButton = () => {
    const isMultiVillage = eventDetails.isMultiVillage;
    const canEdit =
      isEditable &&
      (accountStore.abilities.can("Update", "MultiVillage") || !isMultiVillage);

    return (
      <>
        {canEdit ? (
          <ButtonIcon
            type="primary"
            icon={IconSource.edit}
            onClick={onEditEventClick}
          >
            {t("event:actions.edit")}
          </ButtonIcon>
        ) : (
          <ButtonIcon
            type="primary"
            icon={IconSource.show}
            onClick={onViewEventClick}
          >
            {t("event:actions.view")}
          </ButtonIcon>
        )}
      </>
    );
  };

  const viewOrEditSeriesButton = () => {
    let showViewSeriesButton = false;

    const showEditSeriesButton =
      EventDetailsHelper.canEditEventSeries(eventDetails);

    if (showEditSeriesButton) {
      showViewSeriesButton =
        eventDetails.eventRecurrenceDetails.recurrenceEndDate.date! <
          new Date() ||
        (!accountStore.abilities.can("Update", "MultiVillage") &&
          eventDetails.isMultiVillage);
    }

    return (
      showEditSeriesButton && (
        <>
          {showViewSeriesButton ? (
            <div className={"edit-series-button"}>
              <ButtonIcon
                type="secondary"
                icon={IconSource.show}
                onClick={switchToViewEventSeries}
              >
                {t("event:actions.viewSeries")}
              </ButtonIcon>
            </div>
          ) : (
            <div className={"edit-series-button"}>
              <ButtonIcon
                type="secondary"
                icon={IconSource.edit}
                onClick={switchToEditEventSeries}
              >
                {t("event:actions.editSeries")}
              </ButtonIcon>
            </div>
          )}
        </>
      )
    );
  };

  const formatStatusTag = (): JSX.Element => {
    if (isLoading) {
      return <></>;
    }

    if (EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails)) {
      return EventDetailsHelper.getEventStatusTag(t, eventDetails.eventStatus);
    }

    const preReleasePill = EventDetailsHelper.getPreReleasedPill(
      t,
      eventDetails,
      currentDateTime!,
      isReleased!
    );

    if (preReleasePill) {
      return preReleasePill;
    }

    if (!eventDetails.isPassedEndDate) {
      return (
        <Tag
          text={t(
            isReleased
              ? "event:labels.calendar.released"
              : "event:labels.calendar.unreleased"
          )}
          type={isReleased ? "success" : "neutral"}
        />
      );
    }
    if (!isMultiVillageOptionSelected) {
      return <Tag text={t("event:labels.calendar.past")} type={"light"} />;
    }
    return <></>;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="event-detail-modal"
      data-testid="eventDetailModal"
      centered
    >
      <EventModalHeader
        eventDetails={eventDetails}
        isLoading={isLoading}
        toggle={toggle}
        isEnhancedCalendarEvent={isEnhancedCalendarEvent}
      />
      {eventDetails.isMultiVillage &&
        !accountStore.abilities.can("Update", "MultiVillage") && (
          <Banner
            text={t("event:messages:multiVillageEventBanner")}
            icon={IconSource.info}
          />
        )}
      <ModalBody>
        <div className="details-heading" data-testid="eventDetailModalHeading">
          {t("event:titles.details")}
          {formatStatusTag()}
          {eventDetails.isPassedEndDate &&
            eventDetails.releaseDate.date &&
            isEnhancedCalendarEvent && (
              <Tag
                text={t("event:labels.calendar.preReleased")}
                type="digital"
              />
            )}
        </div>
        {eventDetails.isMultiVillage && (
          <ModalDetailContent
            icon={IconSource.village}
            isLoading={isLoading}
            content={MultiVillageHelper.VillageEllipsis(
              t,
              villageList,
              accountStore.abilities.can("Update", "MultiVillage")
            )}
            dataTestId="eventDetailModal-villages"
          />
        )}
        <ModalDetailContent isLoading={isLoading} icon={IconSource.recent}>
          {eventDetails ? (
            <EventTimeAndDateFormat
              timeAndDate={eventDetails.eventDateDetails.startDate.date!}
              endTime={eventDetails.eventDateDetails.endDate.date!}
              timeZone={eventDetails.eventDateDetails.timeZone}
              isEndTimeVisible={isEnhancedCalendarEvent}
            />
          ) : (
            <></>
          )}
        </ModalDetailContent>
        {isEnhancedCalendarEvent && (
          <>
            <ModalDetailContent
              isLoading={isLoading}
              icon={IconSource.location}
            >
              {isWebLink(eventDetails?.locationDisplayName) ? (
                <a
                  href={eventDetails?.locationDisplayName}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("event:labels.virtualEventSelect")}
                </a>
              ) : (
                <>{eventDetails?.locationDisplayName}</>
              )}
            </ModalDetailContent>
            <ModalDetailContent
              icon={IconSource.description}
              isLoading={isLoading}
              isCentred={false}
              content={eventDetails?.description.text}
            />
          </>
        )}
        {!isLoading && (
          <div className="edit-button">
            {viewOrEditButton()}
            {viewOrEditSeriesButton()}
          </div>
        )}
      </ModalBody>
      {eventDetails?.bookingDetails.hasBookingDeadline &&
        isEnhancedCalendarEvent && (
          <ModalBody className="bookings">
            <div className="details-heading">
              {t("event:titles.bookings")}
              {shouldShowRemainingSpaces && (
                <Tag
                  text={
                    totalBookings === totalSpace
                      ? t("event:labels.booking.fullyBooked")
                      : t("event:labels.booking.spaceAvailable", {
                          count: totalSpace - totalBookings
                        })
                  }
                  type="negative"
                />
              )}
            </div>
            <ModalDetailContent
              icon={IconSource.success}
              isLoading={isLoading}
              isCentred={false}
              children={confirmedBookingCount}
            />
            <ModalDetailContent
              icon={IconSource.replace}
              isLoading={isLoading}
              isCentred={false}
              children={
                <div>
                  {`${eventDetails.bookingSpacesDetails.totalWaitlistedBookings}
                      ${t("event:labels.booking.waitlist")}`}
                </div>
              }
            />
            {eventDetails?.priceDetails?.paidType.text ===
              EventPaidType.fixed && (
              <ModalDetailContent
                icon={IconSource.paid}
                isLoading={isLoading}
                isCentred={false}
                children={
                  <div className="booking-detail">
                    <div>{`${paidPercentage}% ${t(
                      "event:labels.booking.paid"
                    )}`}</div>
                    <div className="extra-info">
                      {t("event:labels.booking.pricePerPerson", {
                        price: eventDetails.priceDetails.price.number
                      })}
                    </div>
                  </div>
                }
              />
            )}

            <div className="edit-button">
              <ButtonIcon type="primary" onClick={switchToManageBooking}>
                {t("event:actions.manageBooking")}
              </ButtonIcon>
            </div>
          </ModalBody>
        )}
    </Modal>
  );
};

export default observer(EventDetailModal);
